import { createContext, useContext } from 'react';

import type {
  TabName,
  VectorFileData,
} from '../components/UI/VectorMemoryModal/vectorModal';
import type { VectorEmbeddingModel, VectorMetadata } from '../types/vector';
import type { NamespaceFile } from '../types/namespace';
import type { NamespaceDimension } from '../types/namespace-dimension';

interface VectorModalContextData {
  spaceId?: string;
  namespaceId: string | undefined;
  tab: TabName;

  name: string;
  embeddingModel: VectorEmbeddingModel;
  searchUsingDimensions: boolean;
  onEmbeddingModelChange: (embeddingModel: VectorEmbeddingModel) => void;
  onNameChange: (newName: string) => void;
  onSearchUsingDimensionsChange: (state?: boolean) => void;

  stagingFiles: Array<VectorFileData>;
  selectedStagingFiles: Array<string>;
  onStagingFilesChange: (files: Array<VectorFileData>) => void;
  onStagingFilesAdd: (files: Array<VectorFileData>) => void;
  onSelectedStagingFilesChange: (selectedFiles: Array<string>) => void;

  storedFiles: Array<NamespaceFile>;
  selectedStoredFiles: Array<string>;
  onStoredFilesChange: (files: Array<NamespaceFile>) => void;
  onSelectedStoredFilesChange: (selectedFiles: Array<string>) => void;

  namespaceDimensions: Array<NamespaceDimension>;
  onNamespaceDimensionsChange: (dimensions: Array<NamespaceDimension>) => void;
  onNamespaceDimensionsDelete: (id: string, title: string) => void;

  onClose: VoidFunction;
  onNamespaceCreate: VoidFunction;
  onNamespaceUpdate: VoidFunction;

  onVectorFileDelete: (fileId: string) => Promise<void>;

  onMetadataCreate: ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<VectorMetadata>;
  }) => void;
  onMetadataDelete: ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<{ name: string }>;
  }) => void;
  onMetadataNameChange: ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<{ oldName: string; newName: string }>;
  }) => void;
  onMetadataValueChange: ({
    filesIds,
    metadatas,
  }: {
    filesIds: Array<string>;
    metadatas: Array<{ name: string; newValue: string }>;
  }) => void;

  isEditing: boolean;
}

const VectorModalContext = createContext<VectorModalContextData>(
  {} as VectorModalContextData,
);

const useVectorModal = () => useContext(VectorModalContext);

export { VectorModalContext, useVectorModal };
